import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Link,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { useNotify } from 'react-admin';
import { getErrorMessage } from '../utils/helperUtils.ts';
import {
  coverageLabel,
  formatPremium,
  formatRenewalDate,
  importRenewals,
  type ColumnMapping,
  type ImportField,
  type ImportOutcome,
  type RenewalImportResult
} from './renewalApi.ts';

// The contact needs an email, a name, or first and last; any of them will do.
const FIELDS: { field: ImportField; label: string; required?: boolean }[] = [
  { field: 'name', label: 'Contact name' },
  { field: 'email', label: 'Email' },
  { field: 'renewalDate', label: 'Renewal date', required: true },
  { field: 'firstName', label: 'First name' },
  { field: 'lastName', label: 'Last name' },
  { field: 'company', label: 'Company' },
  { field: 'coverage', label: 'Line of coverage' },
  { field: 'carrier', label: 'Incumbent carrier' },
  { field: 'premium', label: 'Estimated premium' },
  { field: 'notes', label: 'Notes' }
];

const OUTCOMES: Record<
  ImportOutcome,
  { label: string; color: 'success' | 'info' | 'default' | 'warning' | 'error' }
> = {
  create: { label: 'Add', color: 'success' },
  update: { label: 'Fill in', color: 'info' },
  unchanged: { label: 'On file', color: 'default' },
  duplicate: { label: 'Repeat', color: 'warning' },
  error: { label: 'Unreadable', color: 'error' }
};

const SAMPLE = [
  'Contact,Email,Company,Coverage,Renewal Date,Carrier,Premium,Notes',
  'Dana Whitfield,dana@brightwater.example,Brightwater Logistics,Package,3/12/2027,Chubb,48500,',
  'Lena Ortiz,lena@kestrelmfg.example,Kestrel Manufacturing,Workers Comp,4/2/2027,The Hartford,86000,Wants a quote early'
].join('\n');

const downloadSample = () => {
  const url = URL.createObjectURL(new Blob([SAMPLE], { type: 'text/csv' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = 'renewals-sample.csv';
  link.click();
  URL.revokeObjectURL(url);
};

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

// Nothing is written until the preview has been seen. Rows Forge cannot read
// are listed with the reason, by the line they are on in the spreadsheet.
export const ImportRenewalsDialog = ({
  open,
  onClose,
  onImported
}: {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}) => {
  const notify = useNotify();
  const [file, setFile] = useState<File | null>(null);
  // Undefined until someone picks a column; the API then detects them itself
  const [mapping, setMapping] = useState<ColumnMapping | undefined>(undefined);
  const [rollLapsed, setRollLapsed] = useState(false);
  const [preview, setPreview] = useState<RenewalImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setMapping(undefined);
    setRollLapsed(false);
    setPreview(null);
    setError(null);
  }, [open]);

  useEffect(() => {
    if (!file) return;
    let current = true;
    setBusy(true);
    setError(null);
    importRenewals(file, { dryRun: true, rollLapsed, mapping })
      .then((result) => {
        if (current) setPreview(result);
      })
      .catch((err) => {
        if (!current) return;
        setPreview(null);
        setError(getErrorMessage(err));
      })
      .finally(() => {
        if (current) setBusy(false);
      });
    return () => {
      current = false;
    };
  }, [file, mapping, rollLapsed]);

  const chooseColumn = (field: ImportField, header: string) => {
    const next: ColumnMapping = { ...(mapping ?? preview?.mapping) };
    if (header) next[field] = header;
    else delete next[field];
    setMapping(next);
  };

  const runImport = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const { summary } = await importRenewals(file, { dryRun: false, rollLapsed, mapping });
      const done = [
        summary.create ? `added ${plural(summary.create, 'renewal', 'renewals')}` : null,
        summary.update ? `filled in ${summary.update}` : null,
        summary.newContacts ? `created ${plural(summary.newContacts, 'contact', 'contacts')}` : null
      ].filter(Boolean);
      notify(done.length ? `Imported: ${done.join(', ')}` : 'Nothing new to import', {
        type: 'info'
      });
      onImported();
      onClose();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const summary = preview?.summary;
  const writes = summary ? summary.create + summary.update : 0;
  const lapsedCount = summary ? (rollLapsed ? summary.rolled : summary.lapsed) : 0;
  const columns = mapping ?? preview?.mapping ?? {};

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="lg">
      <DialogTitle>Import renewals</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          A CSV with a row per renewal: the contact&apos;s name or email, the line, the renewal
          date, and the carrier if you have it. Your own column names, lines like GL, BOP or WC, and
          dates like 3/12/2027 work as they are. People are found by email, then by name; anyone
          Forge does not have yet is added as a contact.{' '}
          <Link
            component="button"
            type="button"
            onClick={downloadSample}
            sx={{ verticalAlign: 'baseline' }}
          >
            Download a sample
          </Link>
        </Typography>

        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <Button
            component="label"
            variant="outlined"
            startIcon={<UploadFileOutlinedIcon />}
            disabled={busy}
          >
            {file ? 'Choose another file' : 'Choose CSV file'}
            <input
              hidden
              type="file"
              accept=".csv,text/csv"
              onChange={(event) => {
                const chosen = event.target.files?.[0] ?? null;
                event.target.value = '';
                if (!chosen) return;
                setMapping(undefined);
                setRollLapsed(false);
                setPreview(null);
                setFile(chosen);
              }}
            />
          </Button>
          {file ? (
            <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
              {file.name}
            </Typography>
          ) : null}
          {busy ? <CircularProgress size={18} /> : null}
        </Stack>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {preview ? (
          <Stack spacing={2}>
            {preview.missing.length ? (
              <Alert severity="info">
                Pick the column that holds{' '}
                {preview.missing
                  .map((field) =>
                    field === 'contact' ? 'the contact (an email or a name)' : 'the renewal date'
                  )
                  .join(' and ')}
                .
              </Alert>
            ) : null}

            <Grid container spacing={1.5}>
              {FIELDS.map(({ field, label, required }) => (
                <Grid key={field} size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label={required ? `${label} (required)` : label}
                    value={columns[field] ?? ''}
                    onChange={(event) => chooseColumn(field, event.target.value)}
                    disabled={busy}
                  >
                    <MenuItem value="">
                      <em>Not in this file</em>
                    </MenuItem>
                    {preview.headers.filter(Boolean).map((header) => (
                      <MenuItem key={header} value={header}>
                        {header}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              ))}
            </Grid>

            {preview.warnings.map((warning) => (
              <Alert key={warning} severity="info">
                {warning}
              </Alert>
            ))}

            {summary && summary.rows ? (
              <Box>
                <Typography variant="body2">
                  {[
                    `${summary.create} to add`,
                    summary.update ? `${summary.update} to fill in` : null,
                    summary.unchanged ? `${summary.unchanged} already on file` : null,
                    summary.duplicate ? `${summary.duplicate} repeated in the file` : null,
                    summary.error ? `${summary.error} Forge cannot read` : null
                  ]
                    .filter(Boolean)
                    .join(', ')}
                  .
                  {summary.newContacts
                    ? ` ${plural(summary.newContacts, 'new contact', 'new contacts')} will be created.`
                    : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Renewals already in Forge keep what they have; the file only fills in blanks.
                </Typography>
              </Box>
            ) : null}

            {lapsedCount ? (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rollLapsed}
                    onChange={(event) => setRollLapsed(event.target.checked)}
                    disabled={busy}
                  />
                }
                label={`Roll the ${plural(lapsedCount, 'date', 'dates')} more than 30 days past to the next renewal. Otherwise ${lapsedCount === 1 ? 'it imports' : 'they import'} as lapsed.`}
              />
            ) : null}

            {preview.rows.length ? (
              <TableContainer
                sx={{ maxHeight: 340, border: 1, borderColor: 'divider', borderRadius: 1 }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Line</TableCell>
                      <TableCell>Contact</TableCell>
                      <TableCell>Company</TableCell>
                      <TableCell>Coverage</TableCell>
                      <TableCell>Renews</TableCell>
                      <TableCell>Carrier</TableCell>
                      <TableCell>Result</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.rows.map((row) => {
                      const note =
                        row.error ??
                        [
                          row.rolledFrom
                            ? `Rolled from ${formatRenewalDate(row.rolledFrom)}.`
                            : null,
                          ...row.warnings
                        ]
                          .filter(Boolean)
                          .join(' ');
                      return (
                        <TableRow key={row.line}>
                          <TableCell sx={{ color: 'text.secondary' }}>{row.line}</TableCell>
                          <TableCell>
                            {row.contact.name || '(blank)'}
                            {row.contact.id === null && row.outcome === 'create' ? (
                              <Chip size="small" label="New" variant="outlined" sx={{ ml: 1 }} />
                            ) : null}
                          </TableCell>
                          <TableCell>{row.companyName}</TableCell>
                          <TableCell>{coverageLabel(row.coverage)}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {row.renewalDate ? formatRenewalDate(row.renewalDate) : ''}
                            {row.lapsed ? (
                              <Chip size="small" label="Lapsed" color="warning" sx={{ ml: 1 }} />
                            ) : null}
                          </TableCell>
                          <TableCell>
                            {[row.incumbentCarrier, formatPremium(row.estimatedPremium)]
                              .filter(Boolean)
                              .join(' · ')}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={OUTCOMES[row.outcome].label}
                              color={OUTCOMES[row.outcome].color}
                            />
                            {note ? (
                              <Typography
                                variant="caption"
                                color={row.error ? 'error' : 'text.secondary'}
                                sx={{ display: 'block', mt: 0.5, maxWidth: 260 }}
                              >
                                {note}
                              </Typography>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : null}
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={runImport}
          disabled={busy || !preview || preview.missing.length > 0 || writes === 0}
        >
          {writes ? `Import ${plural(writes, 'renewal', 'renewals')}` : 'Import'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
